import { useNavigate } from 'react-router-dom';
import { examItemAction } from '../../../../core/components/molecules/Exam/Item';
import { IPracticeEnemExam, IPracticeEnemYear } from '../../model/Enem';
import { getPracticesEnem } from '../../queries/Enem';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { practiceEnemPaths } from '../../utils/enemDay';

export const usePracticeEnemListHook = () => {
  const [years, setYears] = useState<string[]>([]);
  const [practiceEnemList, setPracticeEnemList] = useState<IPracticeEnemYear>(
    []
  );
  const { data, isSuccess, isPending } = useQuery({
    queryKey: ['fetch-practice-enem-list'],
    queryFn: () => getPracticesEnem(),
    retry: false,
    refetchOnWindowFocus: false
  });

  const navigate = useNavigate();

  const sortEnemListByYear = (enemList: IPracticeEnemYear) => {
    return Object.keys(enemList).sort((a, b) => parseInt(b) - parseInt(a));
  };

  /**
   * A tentativa que o card representa.
   *
   * `lastTry` só vem preenchido quando existe mais de uma tentativa, então o
   * fallback para `firstTry` cobre quem fez uma vez só.
   *
   * Tem que ser a MAIS RECENTE: o card anuncia "Última tentativa realizada em",
   * e antes exibia os acertos da primeira. Com tentativas antigas em
   * SimulationEntries — que ficaram com os contadores zerados — a primeira era
   * justamente uma delas, e o card mostrava 0 acertos mesmo com a prova feita.
   */
  const getDisplayedAttempt = (practice: IPracticeEnemExam) =>
    practice.lastTry ?? practice.firstTry ?? null;

  const getCorrectAnswersPercentual = (practice: IPracticeEnemExam) => {
    // Aproveitamento = acertos / total de questões (mesma base do feedback).
    // Sem arredondar aqui: o ExamItem formata com 1 casa decimal (toFixed(1)),
    // mantendo o valor idêntico ao do CircularProgressBar no feedback.
    const attempt = getDisplayedAttempt(practice);

    if (!attempt || !practice.totalQuestions) return 0;

    return (100 / practice.totalQuestions) * attempt.correct;
  };

  const getPracticeStatus = (practice: IPracticeEnemExam) => {
    // Formato de dois dias: só é "concluído" quando os dois dias fecharam.
    // Com um dos dois pronto, o card fica "em andamento" para deixar claro que
    // ainda falta metade da prova.
    if (practice.hasSecondDay && practice.days?.length) {
      const finished = practice.days.filter((day) => day.isFinished).length;
      const started = practice.days.some((day) => day.isStarted);

      if (finished === practice.days.length) return 'done';
      if (finished > 0 || started) return 'ongoing';

      // Nenhum dia feito, mas há tentativa concluída: é uma prova respondida no
      // formato de etapa única, antes da divisão em dois dias. Continua sendo
      // "concluído" — senão o card esconderia o desempenho que o aluno já tem.
      return practice.firstTry || practice.lastTry ? 'done' : 'notstarted';
    }

    return practice.firstTry || practice.lastTry ? 'done' : 'notstarted';
  };

  /** Legenda dos dias no card da listagem. */
  const getDaysLabel = (practice: IPracticeEnemExam) => {
    if (!practice.hasSecondDay || !practice.days?.length) return undefined;

    return practice.days
      .map((day) => {
        if (day.isFinished) return `Dia ${day.day}: concluído`;
        if (day.isStarted) return `Dia ${day.day}: em andamento`;

        return `Dia ${day.day}: não iniciado`;
      })
      .join(' · ');
  };

  const getLastAttemptDate = (practice: IPracticeEnemExam) => {
    if (getPracticeStatus(practice) === 'notstarted') {
      return undefined;
    }

    // Nem todo simulado iniciado tem data: no formato de dois dias o aluno pode
    // estar em andamento sem nenhuma tentativa em firstTry/lastTry, e tentativas
    // antigas convertidas podem vir sem data. Junta tudo o que existe e usa a
    // mais recente; sem nenhuma data válida o card simplesmente não exibe.
    const attemptDates = [
      getDisplayedAttempt(practice)?.date,
      ...(practice.days?.map((day) => day.lastAttempt?.date) ?? [])
    ];

    const timestamps = attemptDates
      .filter((date): date is string => Boolean(date))
      .map((date) => new Date(date).getTime())
      .filter((time) => !Number.isNaN(time));

    if (!timestamps.length) {
      return undefined;
    }

    const date = new Date(Math.max(...timestamps));

    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  const handlePracticeAction = async (
    practice: IPracticeEnemExam,
    action: examItemAction
  ) => {
    // No formato de dois dias tudo passa pela tela de escolha, inclusive o
    // gabarito — o aluno precisa dizer de qual dia antes de ver o resultado.
    if (practice.hasSecondDay) {
      goToDaySelectionPage(practice.slug);
      return;
    }

    switch (action) {
      case 'feedback':
        goToFeedbackPage(practice.slug);
        break;

      default:
        goToExamPage(practice.slug);
        break;
    }
  };

  const goToDaySelectionPage = (practiceSlug: string) => {
    navigate(practiceEnemPaths.daySelection(practiceSlug));
  };

  const goToExamPage = (practiceSlug: string) => {
    navigate(practiceEnemPaths.exam(practiceSlug, null));
  };

  const goToFeedbackPage = (practiceSlug: string) => {
    navigate(practiceEnemPaths.feedback(practiceSlug, null));
  };

  const handleSuccess = (data: IPracticeEnemYear) => {
    setYears(sortEnemListByYear(data));
    setPracticeEnemList(data);
  };

  useEffect(() => {
    if (isSuccess) {
      handleSuccess(data);
    }
  }, [isSuccess]);

  return {
    years,
    practiceEnemList,
    isLoading: isPending,
    getLastAttemptDate,
    getPracticeStatus,
    getDaysLabel,
    getDisplayedAttempt,
    getCorrectAnswersPercentual,
    handlePracticeAction,
  };
};
